import * as T from 'three';
import { UniformsLib, UniformsUtils } from 'three';

/** Soft, fogged dust/spray particles simulated on the CPU (a few hundred at most). */
export class Dust {
  points: T.Points; private count: number;
  private pos: Float32Array; private vel: Float32Array; private age: Float32Array; private life: Float32Array; private size: Float32Array; private alpha: Float32Array;
  private next = 0; private carry = 0; private material: T.ShaderMaterial;
  constructor(color: string, count = 260) {
    this.count = count;
    this.pos = new Float32Array(count * 3); this.vel = new Float32Array(count * 3); this.age = new Float32Array(count).fill(99); this.life = new Float32Array(count).fill(1);
    this.size = new Float32Array(count); this.alpha = new Float32Array(count);
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(this.pos, 3)); geo.setAttribute('aSize', new T.BufferAttribute(this.size, 1)); geo.setAttribute('aAlpha', new T.BufferAttribute(this.alpha, 1));
    geo.boundingSphere = new T.Sphere(new T.Vector3(), 1e6);
    this.material = new T.ShaderMaterial({
      transparent: true, depthWrite: false, fog: true,
      uniforms: UniformsUtils.merge([UniformsLib.fog, { uColor: { value: new T.Color(color) }, uScale: { value: 600 } }]),
      vertexShader: /* glsl */`
        attribute float aSize; attribute float aAlpha; uniform float uScale; varying float vAlpha;
        #include <fog_pars_vertex>
        void main() { vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 ); gl_Position = projectionMatrix * mvPosition; gl_PointSize = aSize * uScale / max( 0.5, - mvPosition.z );
        // Fade puffs that drift close to the lens so they never fill the frame.
        vAlpha = aAlpha * smoothstep( 2.0, 6.0, - mvPosition.z );
        #include <fog_vertex>
        }`,
      fragmentShader: /* glsl */`
        uniform vec3 uColor; varying float vAlpha;
        #include <fog_pars_fragment>
        void main() { vec2 c = gl_PointCoord - 0.5; float r = length( c ) * 2.0; float a = smoothstep( 1.0, 0.15, r ) * vAlpha; if ( a < 0.004 ) discard;
          gl_FragColor = vec4( uColor * ( 0.75 + 0.25 * ( 1.0 - r ) ), a );
        #include <fog_fragment>
        }`,
    });
    this.points = new T.Points(geo, this.material); this.points.name = 'dust'; this.points.frustumCulled = false; this.points.renderOrder = 2;
  }
  setViewport(heightPx: number, fov: number) { this.material.uniforms.uScale.value = heightPx / (2 * Math.tan(T.MathUtils.degToRad(fov) / 2)); }
  emit(at: T.Vector3, base: T.Vector3, spread: number, rise: number, size: number, life: number, alpha: number) {
    const i = this.next; this.next = (this.next + 1) % this.count;
    this.pos[i * 3] = at.x + (Math.random() - .5) * .3; this.pos[i * 3 + 1] = at.y + Math.random() * .1; this.pos[i * 3 + 2] = at.z + (Math.random() - .5) * .3;
    this.vel[i * 3] = base.x + (Math.random() - .5) * spread; this.vel[i * 3 + 1] = base.y + rise * (.4 + Math.random() * .8); this.vel[i * 3 + 2] = base.z + (Math.random() - .5) * spread;
    this.age[i] = 0; this.life[i] = life * (.7 + Math.random() * .6); this.size[i] = size * (.6 + Math.random() * .8); this.alpha[i] = alpha;
  }
  /** Emits roughly `rate` particles per second. */
  stream(dt: number, rate: number, at: T.Vector3, base: T.Vector3, spread: number, rise: number, size: number, life: number, alpha: number) {
    this.carry += rate * dt; while (this.carry >= 1) { this.carry -= 1; this.emit(at, base, spread, rise, size, life, alpha); }
  }
  update(dt: number) {
    for (let i = 0; i < this.count; i++) {
      if (this.age[i] >= this.life[i]) { this.alpha[i] = 0; continue; }
      this.age[i] += dt; const t = this.age[i] / this.life[i], drag = Math.exp(-dt * 2.2);
      this.vel[i * 3] *= drag; this.vel[i * 3 + 2] *= drag; this.vel[i * 3 + 1] = this.vel[i * 3 + 1] * drag - .6 * dt;
      this.pos[i * 3] += this.vel[i * 3] * dt; this.pos[i * 3 + 1] += this.vel[i * 3 + 1] * dt; this.pos[i * 3 + 2] += this.vel[i * 3 + 2] * dt;
      this.size[i] += dt * .9; this.alpha[i] *= t > .6 ? Math.exp(-dt * 4) : 1;
      if (t >= 1) this.alpha[i] = 0;
    }
    const g = this.points.geometry; g.attributes.position.needsUpdate = true; g.attributes.aSize.needsUpdate = true; g.attributes.aAlpha.needsUpdate = true;
  }
  dispose() { this.points.geometry.dispose(); this.material.dispose(); }
}
